import React, { useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import { PO_STATUSES } from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { handlePoApiResult } from "../../purchaseOrders/purchaseOrderApi";
import { buildPoListFilters } from "../../purchaseOrders/purchaseOrderListUtils";
import type {
  PurchaseOrderListResult,
  PurchaseOrderListRow,
} from "../../purchaseOrders/purchaseOrderTypes";
import { parsePurchaseOrderListResponse } from "../../purchaseOrders/purchaseOrderTypes";

type AddSnackbarFn = (snackbar: SnackbarData) => void;
type LogoutFn = () => void;

interface EmptyModalState {
  open: boolean;
  childComponent: ReactNode;
}

interface DateFiltersState {
  startDate: Date | null;
  endDate: Date | null;
}

export interface PurchaseOrdersViewModelState {
  loading: boolean;
  purchaseOrders: PurchaseOrderListResult;
  search: string;
  statusFilter: string;
  page: number;
  getPurchaseOrders: (
    searchValue?: string,
    pageValue?: number,
    statusValue?: string,
  ) => Promise<void>;
  askDeletePurchaseOrder: (row: PurchaseOrderListRow) => void;
  statusOptions: { value: string; label: string }[];
  filters: DateFiltersState;
  setFilters: Dispatch<SetStateAction<DateFiltersState>>;
  showDates: boolean;
  setShowDates: Dispatch<SetStateAction<boolean>>;
  emptyModal: EmptyModalState;
  setEmptyModelOpen: Dispatch<SetStateAction<EmptyModalState>>;
}

export function usePurchaseOrdersViewModel(
  logout: LogoutFn,
  addSnackbar: AddSnackbarFn,
): PurchaseOrdersViewModelState {
  const [loading, setLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderListResult>({
    data: [],
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DateFiltersState>({
    startDate: null,
    endDate: null,
  });
  const [showDates, setShowDates] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (message: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={message}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const getPurchaseOrders = async function (
    searchValue?: string,
    pageValue = 1,
    statusValue?: string,
  ) {
    const resolvedStatus = statusValue ?? statusFilter;
    setLoading(true);
    const payload = {
      page: pageValue,
      search: searchValue ?? search ?? "",
      filters: buildPoListFilters(resolvedStatus, filters),
    };
    const callResult = await makePostApiCall("purchaseOrders", payload);
    setLoading(false);

    await handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not load purchase orders.",
      onSuccess: (data) => {
        setPurchaseOrders(parsePurchaseOrderListResponse(data));
        setPage(pageValue);
        if (searchValue !== undefined) {
          setSearch(searchValue ?? "");
        }
        if (statusValue !== undefined) {
          setStatusFilter(statusValue || "");
        }
      },
    });
  };

  const deletePurchaseOrder = async function (id: number) {
    const callResult = await makePostApiCall(`deletePurchaseOrder/${id}`, {});

    await handlePoApiResult<{ message?: string }>(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not delete purchase order.",
      onSuccess: async (data) => {
        setEmptyModelOpen({
          open: false,
          childComponent: null,
        });
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            data?.message || "Purchase order deleted.",
            SnackbarType.Success,
          ),
        );
        await getPurchaseOrders(search, page, statusFilter);
      },
    });
  };

  const askDeletePurchaseOrder = function (row: PurchaseOrderListRow) {
    const label = row.po_number ?? `PO-${row.id}`;
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deletePurchaseOrder(row.id)}
          message={`Are you sure you want to delete ${label}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    loading,
    purchaseOrders,
    search,
    statusFilter,
    page,
    getPurchaseOrders,
    askDeletePurchaseOrder,
    statusOptions: PO_STATUSES.map((s) => ({ value: s, label: s })),
    filters,
    setFilters,
    showDates,
    setShowDates,
    emptyModal,
    setEmptyModelOpen,
  };
}

/** @deprecated Use `usePurchaseOrdersViewModel` */
export const PurchaseOrdersViewModel = usePurchaseOrdersViewModel;
